import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import { AppShell, Button } from '@dj/ui';
import { callable } from '@dj/firebase-client';
import { useClaim, ProviderInfo } from '../state/ClaimContext';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

interface ValidateResponse {
  valid: boolean;
  reason?: 'invalid_code' | 'rate_limited' | 'captcha_failed';
  provider?: ProviderInfo;
}

/**
 * Step 1 — provider code entry. Calls `validateCode` to confirm the code
 * exists + look up the provider's display info (name, price, subsidy %)
 * so the next step can lead with "Sponsored by …".
 *
 * Generic error messaging — never confirms which part of the code is wrong.
 */
export function LandingPage() {
  const navigate = useNavigate();
  const { setProvider } = useClaim();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const submit = async () => {
    const cleaned = code.trim().toUpperCase();
    if (!cleaned) {
      setError('Please enter your code.');
      return;
    }
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the verification challenge.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const validate = callable<
        { code: string; turnstileToken?: string },
        ValidateResponse
      >('validateCode');
      const r = await validate({
        code: cleaned,
        turnstileToken: turnstileToken ?? undefined,
      });
      if (!r.valid || !r.provider) {
        // Reset the widget so the user can try again with a fresh token.
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setError(
          r.reason === 'rate_limited'
            ? "Too many attempts — please wait a moment and try again."
            : r.reason === 'captcha_failed'
            ? "Verification failed — please try again."
            : "That code didn't work. Please check and try again."
        );
        return;
      }
      setProvider(cleaned, r.provider);
      navigate('/identity');
    } catch (e: any) {
      setError(e?.message ?? "We couldn't verify the code right now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell title="Claim your subscription">
      <div className="bg-white rounded-lg shadow p-6 sm:p-8 max-w-lg mx-auto">
        <h1 className="text-2xl font-semibold text-slate-900 mb-3">Welcome</h1>
        <p className="text-slate-600 mb-6">
          If your medical centre or employer has set up Doctor's Journal as a
          benefit, you'll have received a code. Enter it below to activate
          your subscription.
        </p>

        <label className="block text-sm font-medium text-slate-700 mb-1" htmlFor="code">
          Provider code
        </label>
        <input
          id="code"
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="e.g. WESTHEIGHTS24"
          className="w-full border border-slate-300 rounded-md px-3 py-2.5 font-mono tracking-wider uppercase focus:outline-none focus:ring-2 focus:ring-teal-700 focus:border-teal-700 mb-2"
          aria-invalid={!!error}
          aria-describedby={error ? 'code-error' : undefined}
        />
        {error && (
          <p id="code-error" className="text-sm text-red-700 mb-3">{error}</p>
        )}

        {TURNSTILE_SITE_KEY && (
          <div className="mb-3">
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken(null)}
              onError={() => setTurnstileToken(null)}
              options={{ theme: 'light', size: 'flexible' }}
            />
          </div>
        )}

        <Button variant="primary" fullWidth onClick={submit} disabled={busy}>
          {busy ? 'Checking…' : 'Continue'}
        </Button>

        <p className="text-xs text-slate-500 mt-4 text-center">
          Don't have a code? Buy the app from the
          <span className="font-medium"> App Store</span> or
          <span className="font-medium"> Google Play</span>.
        </p>
      </div>
    </AppShell>
  );
}
